/**
 * Shared validation rules for the standalone configuration wizards (PPPC,
 * App Configuration, etc.). Centralizing these keeps the user experience
 * consistent: same patterns, same length caps, same error wording across
 * every entry point.
 *
 * The contract is deliberately strict and Apple-flavoured:
 *  - Identifiers (TCC keys, App Config keys) must start with a letter and
 *    use only [A-Za-z0-9_], up to 64 characters.
 *  - Forbidden characters block obvious injection or path-traversal attempts
 *    even when the regex would technically allow them after trimming.
 *  - Bundle IDs follow Apple's reverse-DNS convention.
 *  - App display names are 1–80 characters.
 *
 * Anything that was previously inlined in PPPC (`pppcValidation.ts`) and the
 * App Config dialog/wizard now points here so divergence cannot creep back
 * in silently.
 */

/** Identifier shape — must start with a letter, then [A-Za-z0-9_], max 64. */
export const WIZARD_IDENTIFIER_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

/** Maximum identifier length (TCC keys, App Config keys). */
export const WIZARD_IDENTIFIER_MAX = 64;

/** Bundle ID — reverse DNS, letters/numbers/dots/hyphens, must contain a dot. */
export const WIZARD_BUNDLE_ID_PATTERN = /^[A-Za-z0-9.\-]+$/;
export const WIZARD_BUNDLE_ID_MIN = 3;

/** App display name length bounds. */
export const WIZARD_APP_NAME_MIN = 1;
export const WIZARD_APP_NAME_MAX = 80;

/** Configuration / policy display name length bounds. */
export const WIZARD_CONFIG_NAME_MIN = 1;
export const WIZARD_CONFIG_NAME_MAX = 80;

/** Code requirement minimum length (PPPC). */
export const WIZARD_CODE_REQUIREMENT_MIN = 10;

/**
 * Substrings that are forbidden inside identifiers regardless of the regex
 * — catches whitespace, quoting, path traversal, HTML, etc.
 */
export const WIZARD_FORBIDDEN_IDENTIFIER_SUBSTRINGS = [
  "..",
  "/",
  "\\",
  "<",
  ">",
  '"',
  "'",
  " ",
  "\n",
  "\t",
];

// ---------------------------------------------------------------------------
// Canonical user-facing messages. Keep the wording identical across surfaces.
// ---------------------------------------------------------------------------

export const WIZARD_VALIDATION_MESSAGES = {
  /** Generic toast shown when Save is clicked but earlier steps have errors. */
  fixHighlightedFieldsToast: "Fix the highlighted fields before saving.",

  /** Bundle ID format errors. */
  bundleIdRequired: "Bundle ID is required.",
  bundleIdInvalid:
    "Bundle ID must contain a dot and only letters, numbers, dots and hyphens.",
  bundleIdMissingFromCatalog: "Selected app is missing a bundle ID.",
  catalogAppRequired: "Select an app from your catalog.",

  /** App display name. */
  appNameRequired: `App name is required (${WIZARD_APP_NAME_MIN}–${WIZARD_APP_NAME_MAX} characters).`,

  /** Configuration / policy display name. */
  configNameRequired: `Configuration name is required (${WIZARD_CONFIG_NAME_MIN}–${WIZARD_CONFIG_NAME_MAX} characters).`,

  /** Identifier (key) errors — used inline next to a row. */
  keyEmpty: "Key is required.",
  keyTooLong: `Key is too long (max ${WIZARD_IDENTIFIER_MAX} characters).`,
  keyForbiddenChars:
    "Key contains invalid characters. Use letters, digits, and underscore only.",
  keyPattern:
    "Key must start with a letter and contain only letters, digits, and underscore.",
  keyDuplicate: "Duplicate key.",

  /** Aggregate / step-level summaries. */
  noKeys: "Add at least one key/value pair.",
  fixHighlightedRows: "Fix the highlighted rows before saving.",
  noTags: "Select at least one tag for deployment.",
  /** Verbose variant for the inline external-empty-state alert. */
  noTagsExternalDetailed:
    "Pick at least one tag so we know which devices should receive this. Tip: use a tag that targets the right Macs — for example an OS, model, or group tag.",
  /** External targets must scope to a customer (or "All Customers") before
   *  any tag list can be assembled. */
  noCustomers:
    "Select at least one customer (or toggle 'All Customers') before saving.",

  /** Code requirement (PPPC). */
  codeRequirementTooShort: `Code Requirement is required and must be at least ${WIZARD_CODE_REQUIREMENT_MIN} characters.`,
} as const;

// ---------------------------------------------------------------------------
// Pure validators — return undefined when valid, a canonical message otherwise.
// ---------------------------------------------------------------------------

/**
 * Validate a single identifier (TCC key or App Config key). Empty rows
 * always return undefined so the caller can render placeholder rows without
 * nagging the user before they've typed anything.
 *
 * @param key   The raw identifier string (will be trimmed before checks).
 * @param treatEmptyAsError When true, an empty string returns the
 *   `keyEmpty` message instead of skipping. Used by surfaces like custom
 *   PPPC services that don't allow blank rows.
 */
export function validateIdentifier(
  key: string,
  options: { treatEmptyAsError?: boolean } = {},
): string | undefined {
  const trimmed = key.trim();
  if (trimmed.length === 0) {
    return options.treatEmptyAsError
      ? WIZARD_VALIDATION_MESSAGES.keyEmpty
      : undefined;
  }
  if (trimmed.length > WIZARD_IDENTIFIER_MAX) {
    return WIZARD_VALIDATION_MESSAGES.keyTooLong;
  }
  if (
    WIZARD_FORBIDDEN_IDENTIFIER_SUBSTRINGS.some((s) => trimmed.includes(s))
  ) {
    return WIZARD_VALIDATION_MESSAGES.keyForbiddenChars;
  }
  if (!WIZARD_IDENTIFIER_PATTERN.test(trimmed)) {
    return WIZARD_VALIDATION_MESSAGES.keyPattern;
  }
  return undefined;
}

/** Validate a Bundle ID. */
export function validateBundleId(bundleId: string): string | undefined {
  const id = bundleId.trim();
  if (id.length === 0) return WIZARD_VALIDATION_MESSAGES.bundleIdRequired;
  if (
    id.length < WIZARD_BUNDLE_ID_MIN ||
    !id.includes(".") ||
    !WIZARD_BUNDLE_ID_PATTERN.test(id)
  ) {
    return WIZARD_VALIDATION_MESSAGES.bundleIdInvalid;
  }
  return undefined;
}

/** Validate the human-readable app display name (1–80 characters). */
export function validateAppName(name: string): string | undefined {
  const trimmed = name.trim();
  if (
    trimmed.length < WIZARD_APP_NAME_MIN ||
    trimmed.length > WIZARD_APP_NAME_MAX
  ) {
    return WIZARD_VALIDATION_MESSAGES.appNameRequired;
  }
  return undefined;
}

/** Validate a configuration / policy display name (1–80 characters). */
export function validateConfigName(name: string): string | undefined {
  const trimmed = name.trim();
  if (
    trimmed.length < WIZARD_CONFIG_NAME_MIN ||
    trimmed.length > WIZARD_CONFIG_NAME_MAX
  ) {
    return WIZARD_VALIDATION_MESSAGES.configNameRequired;
  }
  return undefined;
}

/**
 * Detect duplicate identifiers across a list. Returns the rowIds whose key
 * collides with an earlier row (the first occurrence is considered the
 * canonical one). Empty rows are ignored.
 */
export function findDuplicateKeyRowIds<
  T extends { rowId: string; key: string },
>(rows: T[]): Set<string> {
  const seen = new Map<string, string>();
  const dupes = new Set<string>();
  for (const row of rows) {
    const k = row.key.trim();
    if (!k) continue;
    if (seen.has(k)) {
      dupes.add(row.rowId);
    } else {
      seen.set(k, row.rowId);
    }
  }
  return dupes;
}
